import { ElementRef, inject, Injectable, TemplateRef } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, ValidatorFn, Validators } from '@angular/forms';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { IAppRequest, InterfaceAppServiceRegister } from '@app/app.interface';
import { AppHelper } from '@app/core/app.helper';
import { EFieldsTypes } from '@app/core/enums';
import { TCreateTreeForm, TGetSaveData } from '@app/modules/factor-row/interfaces/factor-row.interface';
import { E_EVENT_TYPES, FactorRowEventService } from '@app/modules/factor-row/services/factor-row-event.service';
import { DialogComponent } from '@components/dialog/component/dialog.component';
import { LANG_DICTIONARY_TOKEN } from '@constants/diConstant';
import { FuiDestroyService } from '@services/destroy.service';
import { BaseHttpService } from '@services/http-base.service';
import { NotifierService } from '@services/notifier.service';
import { NgxSpinnerService } from 'ngx-spinner';
import { BehaviorSubject, Observable } from 'rxjs';
import { first, take, takeUntil, tap } from 'rxjs/operators';
import {
    IAddRelationData,
    IDeleteAllData,
    IDeleteByFDData,
    IDeleteRelationData,
    IFactorRowDesignerRelationsService,
    IGetFieldsRelValidationData,
    IRelationEventData,
} from '../interfaces/factor-row-designer-relations.interface';

@Injectable()
export class FactorRowDesignerRelationsService implements IFactorRowDesignerRelationsService {
    readonly #destroy$ = inject(FuiDestroyService);
    private readonly dictionary = inject(LANG_DICTIONARY_TOKEN);
    private readonly http = inject(BaseHttpService);
    private readonly factorRowEventService = inject(FactorRowEventService, { optional: true });

    /**
     * Ссылка на контроллер
     */
    url: string = '/designer_relations/front_designer_relations_table';

    /**
     * Набор структур полей связи
     */
    relationsFields = new BehaviorSubject<{ [key: string]: any }>({});

    /**
     * Ссылка на диалог
     */
    relationDialogRef: MatDialogRef<any>;

    /**
     * Переданная извне функция для создания полей динамической формы
     */
    createTreeForm: TCreateTreeForm = null;

    /**
     * Переданная извне функция для получения данных полей динамической формы
     */
    getSaveData: TGetSaveData = null;

    readonly #spinner: NgxSpinnerService = inject(NgxSpinnerService);
    readonly #notifier: NotifierService = inject(NotifierService);
    readonly #formBuilder: FormBuilder = inject(FormBuilder);
    readonly #dialog: MatDialog = inject(MatDialog);

    /**
     * Получение данных таблицы связей
     * @param data данные
     */
    public getLists(data: any): Observable<any> {
        return this.http.client.post<any>(this.http.url + this.url + '/fetch_table_data', data, this.http.options);
    }

    /**
     * Удаление записей
     * @param data данные
     */
    public remove(data: IDeleteRelationData): Observable<any> {
        return this.http.client.post<any>(this.http.url + this.url + '/delete', data, this.http.options).pipe(
            take(1),
            tap((result) => {
                if (result.hasOwnProperty('is_error') && result.is_error === false) {
                    // Инициируем событие
                    const eventData: IRelationEventData = {
                        factor_id: data.factor_id,
                        relation_id: data.relation_id,
                    };
                    this.factorRowEventService?.initEvent(E_EVENT_TYPES.RELATIONS, {
                        action: 'remove',
                        data: eventData,
                    });
                }
            }),
        );
    }

    /**
     * Удаление записей по uuid связанных FD
     * @param data
     * @param options
     */
    public removeByFD(data: IDeleteByFDData, options = { eventEmit: true }): Observable<IAppRequest> {
        return this.http.client
            .post<IAppRequest>(this.http.url + this.url + '/deleteByFD', data, this.http.options)
            .pipe(
                take(1),
                tap((result) => {
                    if (result && result.hasOwnProperty('is_error') && result.is_error === false && options.eventEmit) {
                        // Инициируем событие
                        const eventData: IRelationEventData = {
                            factor_id: data.factor_id,
                            relation_id: data.relation_id,
                        };
                        this.factorRowEventService?.initEvent(E_EVENT_TYPES.RELATIONS, {
                            action: 'remove',
                            data: eventData,
                        });
                    }
                }),
            );
    }

    /**
     * Удаление всех связей для указанного поля записи реестра
     * @param data данные
     */
    public removeAll(data: IDeleteAllData): Observable<IAppRequest> {
        return this.http.client.post<any>(this.http.url + this.url + '/deleteAll', data, this.http.options).pipe(
            take(1),
            tap((result) => {
                if (result.hasOwnProperty('is_error') && result.is_error === false) {
                    // Инициируем событие
                    const eventData: IRelationEventData = {
                        factor_id: data.factorId,
                        relation_id: data.relationId,
                    };
                    this.factorRowEventService?.initEvent(E_EVENT_TYPES.RELATIONS, {
                        action: 'remove',
                        data: eventData,
                    });
                }
            }),
        );
    }

    /**
     * Получение настроек таблицы
     */
    public getTableSettings(data): Observable<any> {
        return this.http.client.post<any>(this.http.url + this.url + '/fetch_table_settings', data, this.http.options);
    }

    /**
     * Сохранение настроек таблицы
     */
    public saveTableSettings(data: any): Observable<any> {
        return this.http.client.post<any>(this.http.url + this.url + '/save_table_settings', data, this.http.options);
    }

    /**
     * Очистка настроек таблицы
     */
    public clearTableSettings(data: any): Observable<any> {
        return this.http.client.post<any>(this.http.url + this.url + '/clear_table_settings', data, this.http.options);
    }

    /**
     * Получение структуры полей связи
     */
    public getPropertiesRelation(data: any): Observable<any> {
        return this.http.client.post<any>(this.http.url + this.url + '/get_props_relation', data, this.http.options);
    }

    /**
     * Получение записи
     * @param data данные
     */
    public getRelationItem(data: any): Observable<any> {
        return this.http.client.post<any>(this.http.url + this.url + '/get_item', data, this.http.options);
    }

    /**
     * Получение данных по связи
     * @param data данные
     */
    public getRelationData(data: any): Observable<any> {
        return this.http.client.post<any>(this.http.url + this.url + '/get_relation_data', data, this.http.options);
    }

    /**
     * Сохранение целой записи
     * @param data данные
     */
    public save(data: any): Observable<any> {
        return this.http.client.post<any>(this.http.url + this.url + '/save', data, this.http.options);
    }

    /**
     * Получение данных истории изменений
     * @param data данные для получения истории
     */
    getHistoryData(data: { factor_id: any; language: string; type: any; factor_data_id: string; order: number }) {
        return this.http.client.post<any>(this.http.url + this.url + '/get_history', data, this.http.options);
    }

    /**
     * Добавление двойной связи без атрибутов
     * @param data данные для добавления
     */
    addRelation(data: IAddRelationData, skipEvent = false): Observable<IAppRequest> {
        return this.http.client
            .post<IAppRequest>(this.http.url + this.url + '/addDualRelations', data, this.http.options)
            .pipe(
                tap((result) => {
                    if (result?.is_error === false && !skipEvent) {
                        // Инициируем событие
                        const eventData: IRelationEventData = {
                            factor_id: data.factorId,
                            relation_id: data.relationId,
                        };
                        this.factorRowEventService?.initEvent(E_EVENT_TYPES.RELATIONS, {
                            action: 'add',
                            data: eventData,
                        });
                    }
                }),
            );
    }

    /**
     * Получение полей связи для валидации
     * @param data данные для валидации
     */
    public getFieldsRelValidation(data: IGetFieldsRelValidationData[]): Observable<any> {
        return this.http.client.post<any>(
            this.http.url + this.url + '/get_fields_rel_validation',
            { data },
            this.http.options,
        );
    }

    /**
     * Редактирование записи связи
     * @param callback Выполнить после окончания редактирования
     * @param excludeIds Исключить возможность выбора записей реестра с этими ИД
     * @param event данные записи
     * @param item информация по связи
     * @param formData Изначальные данные формы
     * @param relationDialog Ссылка на объект диалога
     */
    openEditRelations(
        callback: () => void,
        excludeIds: string[],
        event: any,
        item: any,
        formData: { factor_id: string | number; factor_data_id: string; scheduler_date?: string },
        relationDialog: TemplateRef<any>,
    ) {
        const relationsField = this.relationsFields.value[item.id];

        /* Теперь кнопка редактировать может вернуть массив элементов */
        const eventData = (Array.isArray(event) ? event[0] : event) ?? null;

        // Проверка наличия редактируемых данных
        if (eventData && eventData.drdata) {
            this.#spinner.show();
            this.getRelationItem({
                id: formData.factor_data_id,
                factor_id: relationsField?.factor_id,
                relation_id: relationsField?.relation_id,
                relation_data_id: eventData.drdata,
            })
                .pipe(
                    first(),
                    tap(() => this.#spinner.hide()),
                    takeUntil(this.#destroy$),
                )
                .subscribe({
                    next: (result: IAppRequest) => {
                        if (result.is_error) {
                            this.#notifier.notify('error', result.message);
                            return;
                        }

                        // Открыть диалог с формой связи с атрибутами на редактирование
                        const form = this.createFormRelation(result.data, item.id, formData);
                        this.openDialogRelations(callback, excludeIds, item, form, relationDialog, true);
                    },
                    error: (err: unknown) => this.#notifier.exception(err),
                });
        } else {
            // Предвыбрать текущую реестровую запись в подходящем поле связи на форме
            if (relationsField?.children?.length) {
                const firstSameKind = (relationsField.children as any[]).find(
                    (x) => Number(x.reestr_id) === Number(formData.factor_id),
                );
                if (firstSameKind) {
                    firstSameKind.value = formData.factor_data_id;
                }
            }

            // Открыть диалог с формой связи с атрибутами на добавление
            const form = this.createFormRelation(relationsField, item.id, formData);
            this.openDialogRelations(callback, excludeIds, item, form, relationDialog, false);
        }
    }

    /**
     * Создание формы для связей
     * @param field данные по форме
     * @param tableId ИД поля формы
     * @param formData Изначальные данные формы
     */
    createFormRelation(
        field: any,
        tableId: string | number,
        formData: { factor_id: string | number; factor_data_id: string },
    ): FormGroup {
        const form = this.#formBuilder.group({
            id: field.id,
            factor_id: field.factor_id,
            table_id: tableId,
            current_factor_id: null,
            relation_id: field.relation_id,
            relation_data_id: field.relation_data_id,
            register_id: formData.factor_id,
            factor_data_id: formData.factor_data_id,
            values: this.#formBuilder.group({}),
        });
        const values = form.get('values') as FormGroup;
        this.createTreeForm(field.children, values, null, []);
        for (const reestrField of this.getReestrFormRelationFields(field.children, values)) {
            reestrField.addValidators([
                Validators.required,
                this.getCurrentRegistryValidator(formData.factor_data_id, field.children, values),
            ]);
            reestrField.updateValueAndValidity();
        }
        return form;
    }

    /**
     * Получение полей формы которые соответствуют полям связи типа "Реестр"
     * @param fields данные полей связи
     * @param fieldGroup поля реактивной формы ангуляра
     */
    getReestrFormRelationFields(fields: any[], fieldGroup: FormGroup): AbstractControl[] {
        return fields
            .filter((x) => x.data_type === EFieldsTypes.reestr)
            .map((x) => fieldGroup.controls[x.id + ''] ?? null)
            .filter((x) => x !== null) as AbstractControl[];
    }

    /**
     * Получение кастомного валидатора для проверки, выбрана ли на форме связи текущая реестровая запись
     * @param factorDataId ИД текущей реестровой записи
     * @param fields данные полей связи
     * @param fieldGroup поля реактивной формы ангуляра
     */
    getCurrentRegistryValidator(factorDataId: string, fields: any[], fieldGroup: FormGroup): ValidatorFn {
        return (_control: AbstractControl) =>
            this.getReestrFormRelationFields(fields, fieldGroup)
                .map((x) => x?.value ?? null)
                .findIndex((x) => x === factorDataId) === -1
                ? { noCurrentRegistry: true }
                : null;
    }

    /**
     * Получение подсказки, выбрана ли на форме связи текущая реестровая запись
     * @param factorDataId ИД текущей реестровой записи
     * @param item данные поля связи
     * @param fieldGroup поля формы связи с атрибутами
     */
    getReestrFieldHint(factorDataId: string, item: any, fieldGroup: FormGroup): string {
        const controlValue = fieldGroup.get(`${item.id}`)?.value ?? null;
        return factorDataId === controlValue ? this.dictionary.relation_contain_current_registry_entry : '';
    }

    /**
     * Открытие диалога формы связи
     * @param table Элемент таблицы
     * @param item информация по связи
     * @param form форма
     * @param relationDialog Ссылка на объект диалога
     * @param editFlag - флаг редактирования
     */
    openDialogRelations(
        callback: () => void,
        excludeIds: string[],
        item: any,
        form: FormGroup,
        relationDialog: TemplateRef<any>,
        editFlag: boolean = false,
    ) {
        this.relationDialogRef = DialogComponent.commonDialog(this.#dialog, relationDialog, {
            disableClose: true,
            autoFocus: false,
            width: '800px',
            data: {
                title: item.name ? item.name : this.dictionary.system_edit,
                properties: this.relationsFields.value[item.id]?.children ?? [],
                form,
                editFlag,
                list_guids: excludeIds,
                uuid: null,
            },
        });

        this.relationDialogRef
            .afterClosed()
            .pipe(take(1), takeUntil(this.#destroy$))
            .subscribe((result) => {
                if (result.save) {
                    callback();
                }
            });
    }

    /**
     * Обработка структуры полей связи
     * @param factorId ИД поля реестра
     * @param relations Данные по полям связей
     * @param registers Справочники
     */
    processRelationData(
        factorId: number,
        relations: any[],
        registers: { [key: string]: InterfaceAppServiceRegister[] },
    ) {
        const relationsFields: { [key: string]: any } = {};
        relations.forEach((value) => {
            if (value?.rubricators) {
                Object.keys(value.rubricators).forEach((k) => {
                    registers[k] = value.rubricators[k];
                });
            }
            const children = (value.children as any[]) ?? [];
            const relationReestr = children.find((x) => x.data_type === 'reestr' && Number(x.reestr_id) !== factorId);
            value.relationReestrId = Number(relationReestr?.reestr_id ?? factorId);
            relationsFields[value.id] = value;
        });
        this.relationsFields.next(relationsFields);
    }

    /**
     * Действие с диалогом горячими кнопками
     * @param event событие
     * @param form форма
     * @param formRef ссылка на форму
     * @param factorDataId ИД записи реестра
     * @param getSaveData Получение данных для сохранения
     * @param editFlag Флаг редактирования
     */
    keyDownRelationFunction(
        event: KeyboardEvent,
        form: any,
        formRef: ElementRef | any,
        factorDataId: string,
        editFlag: boolean = false,
    ) {
        if (event.code === 'Enter') {
            this.saveRelationDialog(form, formRef, factorDataId, editFlag);
        }
    }

    /**
     * Сохранение данных записи
     * @param form форма
     * @param formRef ссылка на форму
     * @param factorDataId ИД записи реестра
     * @param editFlag Флаг редактирования
     */

    saveRelationDialog(
        form: FormGroup | any,
        formRef: ElementRef | any,
        factorDataId: string,
        editFlag: boolean = false,
    ) {
        for (const controlName of Object.keys(form.controls.values.controls)) {
            form.controls.values.controls[controlName]?.updateValueAndValidity();
        }
        if (form.invalid) {
            AppHelper.validateAllFormFields(form);
            setTimeout(() => {
                const target = formRef?.querySelector(
                    '.mat-select.ng-invalid, .mat-input-element.ng-invalid, .mat-checkbox-input.ng-invalid',
                );
                AppHelper.focusInput(target);
            }, 0);
            return;
        }

        this.#spinner.show();
        const data = Object.assign({}, form?.getRawValue());
        data.factor_id = data.relation_id;
        data.factor_data_id = data.relation_data_id;
        data.data_id = factorDataId;
        const values = Object.assign({}, data.values);
        data.language_data = {};
        data.gallery = {};
        data.edit_flag = editFlag;
        data.values = this?.getSaveData(values, data.gallery, data.language_data, null, null);

        this.save(data)
            .pipe(
                first(),
                tap(() => this.#spinner.hide()),
                takeUntil(this.#destroy$),
            )
            .subscribe({
                next: (result: IAppRequest) => {
                    if (result && result.is_error === false) {
                        this.#notifier.success(this.dictionary.system_data_successfully_saved);
                        this.relationDialogRef.close({ save: true });

                        if (!data.edit_flag) {
                            const eventData: IRelationEventData = {
                                factor_id: +data.table_id,
                                relation_id: +data.relation_id,
                            };
                            this.factorRowEventService?.initEvent(E_EVENT_TYPES.RELATIONS, {
                                action: 'add',
                                data: eventData,
                            });
                        }
                    } else {
                        AppHelper.setErrorForm(result, form, formRef, this.#notifier);
                    }
                },
                error: (err: unknown) => {
                    this.#notifier.exception(err);
                },
            });
    }

    /** Закрытие диалога редактирования связи с атрибутами */
    closeRelationDialog() {
        this.relationDialogRef.close({ save: false });
    }

    /**
     * Проверка текущей карточки в связи
     * @param value текущее значение
     * @param factorDataId ИД записи реестра
     */
    isCurrentRelation(value: any, factorDataId: string): boolean {
        return value?.id && value.id === factorDataId;
    }

    /**
     * Получаем уиды карточек, содержащиеся в связях
     * @param filteredData данные таблицы связей
     */
    getExcludeIds(filteredData: any): string[] {
        if (!filteredData || !filteredData.length) {
            return [];
        }
        // Выбираем из обьекта поля, соответствующие регулярке
        const regexp = /linkfdi/;
        const arr = [];
        filteredData.forEach((key) => {
            Object.keys(key).forEach((item) => {
                if (regexp.test(item) || item === 'fdata') {
                    arr.push(key[item]);
                }
            });
        });

        // Удаляем повторяющиеся значения
        return arr.filter((item, index) => {
            return arr.indexOf(item) === index && item !== null;
        });
    }

    /**
     * Получить настройки связи
     * @param data - данные
     */
    public getFactorRelationSettings(data: { id: number }): Observable<IAppRequest> {
        return this.http.client.post<IAppRequest>(
            this.http.url + '/factor_constructor/getFieldSetting',
            data,
            this.http.options,
        );
    }

    /**
     * Сохранить настройки связи
     * @param data - данные
     */
    public setFactorRelationSettings(data: { id: number }): Observable<IAppRequest> {
        return this.http.client.post<IAppRequest>(
            this.http.url + '/factor_constructor/setFactorRelationSettings',
            data,
            this.http.options,
        );
    }
}
